/**
 * Age gate — REJECT only, and ONE BIT.
 *
 * The gate is a single affirmation ("I confirm I am N or over"), evaluated by the
 * person making it. No date of birth is collected, so none is evaluated, derived
 * or discarded: there is no age data anywhere in this path for anyone, passed or
 * refused. `recordAgeGateBlock` takes no age argument, by design and by function
 * signature.
 *
 * There is no parental-consent route, no appeal, and no override.
 */
import { supabase } from "@/integrations/supabase/client";


export interface AgeGateConfig {
  enabled: boolean;
  thresholdYears: number;
}

const FALLBACK: AgeGateConfig = { enabled: true, thresholdYears: 18 };

/** Config resolver — threshold is CONFIG (clamped server-side), never hardcoded. */
export async function getAgeGateConfig(): Promise<AgeGateConfig> {
  try {
    const { data, error } = await supabase.rpc("effective_age_gate");
    const row = Array.isArray(data) ? (data[0] as any) : (data as any);
    if (error || !row) return FALLBACK;
    return {
      enabled: row.enabled ?? true,
      thresholdYears: Number(row.threshold_years ?? FALLBACK.thresholdYears),
    };
  } catch {
    // Fail CLOSED: if the config can't be read, the gate still applies.
    return FALLBACK;
  }
}

/**
 * Records the AFFIRMATION as a consent, on the user scope when signed in and on
 * the guest session otherwise. Stores the outcome and the threshold in force —
 * no date of birth, no derived age. This is the record the generation gate in
 * `generate-avatar` checks, so the client is never the only control.
 */
/**
 * Server truth for "does the CURRENT scope hold the affirmation?" — user scope
 * when signed in, guest session otherwise. The browser mark cannot answer this:
 * it survives sign-in and new sessions, which is what let the generation gate
 * fire on someone who had ticked the box.
 */
export async function getAgeAffirmationState(
  token: string | null,
): Promise<{ affirmed: boolean; blocked: boolean; scope: string | null } | null> {
  try {
    const { data, error } = await supabase.rpc("age_affirmation_state", { p_token: token ?? "" });
    if (error || !data) return null;
    const row = data as any;
    return {
      affirmed: row.affirmed === true,
      blocked: row.blocked === true,
      scope: row.scope ?? null,
    };
  } catch {
    return null;
  }
}

export async function recordAgeAffirmation(token: string | null) {
  return supabase.rpc("record_age_affirmation", { p_token: token ?? "" });
}

/**
 * Records that a refusal happened. Retains: timestamp, programme, the threshold
 * in force, the outcome, and the coarse session reference already held.
 * Retains NOT: date of birth, computed age, name, email, IP, selfie.
 */
export async function recordAgeGateBlock(token: string | null, programId?: string | null) {
  return supabase.rpc("record_age_gate_block", {
    p_token: token ?? "",
    p_program_id: programId ?? null,
  });
}

/** Admin figure: COUNT ONLY. No timestamps are returned to any UI. */
export async function getAgeGateBlockedCount(): Promise<number | null> {
  const { data, error } = await supabase.rpc("age_gate_blocked_count");
  if (error) return null;
  return data == null ? null : Number(data);
}
